import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Folder, FileText, Wand2, Edit, Trash2 } from 'lucide-react';
import { HierarchyNode } from '../types';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface HierarchyTreeProps {
  nodes: HierarchyNode[];
  onNodeClick: (node: HierarchyNode) => void;
  selectedNodeId?: string;
  className?: string;
  showActions?: boolean;
  onAITutorialEdit?: (tutorialId: string) => void;
  onManualTutorialEdit?: (tutorialId: string) => void;
  onDeleteTutorial?: (tutorialId: string, tutorialTitle: string) => void;
  deletingTutorialId?: string | null;
}

export const HierarchyTree: React.FC<HierarchyTreeProps> = ({ 
  nodes, 
  onNodeClick, 
  selectedNodeId,
  className,
  showActions = false,
  onAITutorialEdit,
  onManualTutorialEdit,
  onDeleteTutorial,
  deletingTutorialId = null
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminOrInstructor = user?.roles?.some((role: string) => 
    role === 'admin' || role === 'instructor'
  ) || false;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expanded);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpanded(newExpanded);
  };

  const renderNode = (node: HierarchyNode, depth: number = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded.has(node.id);
    const isSelected = selectedNodeId === node.id;

    return (
      <div key={node.id}>
        <div
          className={cn(
            "flex items-center py-2.5 px-4 hover:bg-gray-50 cursor-pointer transition-all duration-200 rounded-lg mx-1 group",
            isSelected && "bg-gradient-to-r from-primary-50 to-primary-100 border-l-4 border-primary-600 shadow-sm",
            className
          )}
          style={{ paddingLeft: `${depth * 20 + 16}px` }}
          onClick={() => onNodeClick(node)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => toggleExpand(node.id, e)}
              className="mr-2 p-1 hover:bg-gray-200 rounded flex items-center justify-center transition-colors"
              aria-label={isExpanded ? "Collapse" : "Expand"}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          ) : (
            <span className="w-6" />
          )}
          
          {node.type === 'collection' || node.type === 'category' ? (
            <Folder className={cn(
              "w-4 h-4 mr-3 flex-shrink-0 transition-colors",
              isSelected ? "text-primary-600" : "text-gray-500 group-hover:text-primary-600"
            )} />
          ) : (
            <FileText className={cn(
              "w-4 h-4 mr-3 flex-shrink-0 transition-colors",
              isSelected ? "text-primary-600" : "text-gray-500 group-hover:text-gray-700"
            )} />
          )}
          
          <span className={cn(
            "flex-1 truncate text-sm font-medium transition-colors",
            isSelected ? "text-primary-900 font-semibold" : "text-gray-700 group-hover:text-gray-900"
          )}>
            {node.name}
          </span>
          
          {node.tutorialCount !== undefined && node.tutorialCount > 0 && (
            <span className={cn(
              "text-xs ml-2 flex-shrink-0 px-2 py-0.5 rounded-full font-medium",
              isSelected 
                ? "bg-primary-200 text-primary-800" 
                : "bg-gray-100 text-gray-600 group-hover:bg-gray-200"
            )}>
              {node.tutorialCount}
            </span>
          )}
          {/* Action buttons for tutorials */}
          {node.type === 'tutorial' && isAdminOrInstructor && (showActions || onAITutorialEdit) && (
            <div 
              className="flex gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              {onAITutorialEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAITutorialEdit(node.tutorial!.id);
                  }}
                  className="p-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  title="Edit with AI"
                >
                  <Wand2 className="w-3 h-3" />
                </button>
              )}
              {onManualTutorialEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onManualTutorialEdit(node.tutorial!.id);
                  }}
                  className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  title="Manual Edit"
                >
                  <Edit className="w-3 h-3" />
                </button>
              )}
              {onDeleteTutorial && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteTutorial(node.tutorial!.id, node.tutorial!.title);
                  }}
                  disabled={deletingTutorialId === node.tutorial!.id}
                  className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete Tutorial"
                >
                  {deletingTutorialId === node.tutorial!.id ? (
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                </button>
              )}
            </div>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (nodes.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500 text-center">
        No collections found
      </div>
    );
  }

  return (
    <div className="hierarchy-tree">
      {nodes.map(node => renderNode(node))}
    </div>
  );
};

